import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import English translation namespaces
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSurvey from './locales/en/survey.json';
import enBuilder from './locales/en/builder.json';
import enDashboard from './locales/en/dashboard.json';
// import enQuiz from './locales/en/quiz.json'; // REMOVED - module disabled
// import enAssessment from './locales/en/assessment.json'; // REMOVED - module disabled
import enValidation from './locales/en/validation.json';
import enError from './locales/en/error.json';
import enLanding from './locales/en/landing.json';
import enFeatures from './locales/en/features.json';
import enAbout from './locales/en/about.json';
import enFaq from './locales/en/faq.json';
import enBilling from './locales/en/billing.json';
import enExam from './locales/en/exam.json';

// Import Albanian translation namespaces
import sqCommon from './locales/sq/common.json';
import sqAuth from './locales/sq/auth.json';
import sqSurvey from './locales/sq/survey.json';
import sqBuilder from './locales/sq/builder.json';
import sqDashboard from './locales/sq/dashboard.json';
// import sqQuiz from './locales/sq/quiz.json'; // REMOVED - module disabled
// import sqAssessment from './locales/sq/assessment.json'; // REMOVED - module disabled
import sqValidation from './locales/sq/validation.json';
import sqError from './locales/sq/error.json';
import sqLanding from './locales/sq/landing.json';
import sqFeatures from './locales/sq/features.json';
import sqAbout from './locales/sq/about.json';
import sqFaq from './locales/sq/faq.json';
import sqBilling from './locales/sq/billing.json';
import sqExam from './locales/sq/exam.json';

// Namespace definitions
export const namespaces = [
  'common',
  'auth',
  'survey',
  'builder',
  'dashboard',
  // 'quiz', // REMOVED - module disabled
  // 'assessment', // REMOVED - module disabled
  'validation',
  'error',
  'landing',
  'features',
  'about',
  'faq',
  'billing',
  'exam',
] as const;

export type Namespace = (typeof namespaces)[number];

export const defaultNS: Namespace = 'common';

// Resources configuration
export const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    survey: enSurvey,
    builder: enBuilder,
    dashboard: enDashboard,
    // quiz: enQuiz, // REMOVED - module disabled
    // assessment: enAssessment, // REMOVED - module disabled
    validation: enValidation,
    error: enError,
    landing: enLanding,
    features: enFeatures,
    about: enAbout,
    faq: enFaq,
    billing: enBilling,
    exam: enExam,
  },
  sq: {
    common: sqCommon,
    auth: sqAuth,
    survey: sqSurvey,
    builder: sqBuilder,
    dashboard: sqDashboard,
    // quiz: sqQuiz, // REMOVED - module disabled
    // assessment: sqAssessment, // REMOVED - module disabled
    validation: sqValidation,
    error: sqError,
    landing: sqLanding,
    features: sqFeatures,
    about: sqAbout,
    faq: sqFaq,
    billing: sqBilling,
    exam: sqExam,
  },
} as const;

// Supported languages configuration
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'sq', name: 'Albanian', nativeName: 'Shqip' },
] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number]['code'];

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    ns: namespaces,
    fallbackLng: 'en',
    supportedLngs: supportedLanguages.map((lang) => lang.code),

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'datapulse_language',
      caches: ['localStorage'],
    },

    // Development helpers
    debug: import.meta.env.DEV,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lngs, ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`Missing translation: ${ns}:${key} for languages: ${lngs}`);
      }
    },

    react: {
      useSuspense: true,
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span'],
    },
  });

export default i18n;
